import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

export default function EngravingOrderPrintView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const res = await axios.get(`/api/engraving-orders/${id}/print-data`, config);
        setData(res.data);
        // Fetch tenant logo
        const tenantSlug = localStorage.getItem('tenantSlug') || 'demo-corp';
        try {
          const logoRes = await axios.get(`/api/tenants/${tenantSlug}/logo`, config);
          if (logoRes.data?.logoUrl) setLogoUrl(logoRes.data.logoUrl);
        } catch {}
      } catch (err: any) {
        setError(err.response?.data?.error || 'Error al cargar los datos');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (loading) return <div className="flex items-center justify-center h-screen text-gray-500">Cargando...</div>;
  if (error || !data) return <div className="flex items-center justify-center h-screen text-red-500">{error || 'Sin datos'}</div>;

  const { engravingOrder: eg, productionOrder: po } = data;

  let pn: any = {};
  try { if (po?.notes) pn = JSON.parse(po.notes); } catch {}
  const ps = pn.processSpecs || {};

  const colores: string[] = Array.isArray(eg.colores) ? eg.colores : [];

  const todayStr = new Date().toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: '2-digit' });
  const deliveryStr = eg.fechaEntrega
    ? new Date(eg.fechaEntrega + 'T12:00:00').toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: '2-digit' })
    : '';

  const egAnchoInd = eg.anchoIndividual || ps.individualWidth || '';
  const egBobEje   = eg.bobinasAlEje   || ps.bobinCount       || '';
  const egRepInd   = eg.repeticionIndividual || ps.individualRepetition || '';
  const egBobDes   = eg.bobinasAlDesarrollo  || ps.repetitionCount     || '';
  const egRodillo  = eg.rodilloFinal   || ps.selectedMangaCircunferencia || ps.cylinderCircumference || '';
  const egFlexo    = eg.flexo          || ps.coloresCount || ps.flexoCount || '';
  const egBarCode  = eg.codigoBarras   || po?.codigoBarras || ps.barCode || ps.codigoBarras || '';

  const iw = parseFloat(egAnchoInd) || 0;
  const bc = parseFloat(egBobEje)   || 0;
  const ir = parseFloat(egRepInd)   || 0;
  const rc = parseFloat(egBobDes)   || 0;
  const anchoTotal = iw && bc ? (iw * bc).toFixed(2) : '';
  const repTotal   = ir && rc ? (ir * rc).toFixed(2) : '';

  const hasPhotocell = ps.photocell?.width || ps.photocell?.height;

  const val = (v: any) => (v !== null && v !== undefined && v !== '' && v !== false) ? String(v) : '';

  const LABEL = 'text-[10px] font-bold text-gray-600 uppercase';
  const VALUE = 'text-[13px] font-bold text-gray-900';

  return (
    <div className="min-h-screen bg-white">
      <div className="no-print fixed top-0 left-0 right-0 bg-gray-800 text-white p-3 flex gap-3 items-center z-50 shadow-lg">
        <button onClick={() => navigate(-1)} className="px-3 py-1.5 bg-gray-600 hover:bg-gray-500 rounded text-sm">← Volver</button>
        <button onClick={() => window.print()} className="px-4 py-1.5 bg-blue-600 hover:bg-blue-500 rounded text-sm font-medium">🖨️ Imprimir</button>
        <span className="text-gray-300 text-sm ml-4">Orden de Grabado — {po?.orderNumber} | {po?.customerName}</span>
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { margin: 0; padding: 0; }
          @page { size: letter portrait; margin: 0.35in 0.4in; }
          body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
        }
        table { border-collapse: collapse; width: 100%; }
        td, th { border: 1px solid #555; padding: 2px 5px; vertical-align: middle; }
      `}</style>

      <div className="pt-16 print:pt-0 px-8 py-4 print:px-0 print:py-0 max-w-[760px] mx-auto font-sans">

        {/* ── HEADER ── */}
        <table style={{ marginBottom: 0 }}>
          <tbody>
            <tr>
              <td rowSpan={2} style={{ width: 110 }} className="text-center">
                {logoUrl
                  ? <img src={logoUrl} alt="Logo" className="max-h-14 max-w-[100px] object-contain mx-auto" />
                  : (
                    <>
                      <div className="font-black text-[19px] tracking-tight text-gray-900">Rototec</div>
                      <div className="text-[9px] font-semibold text-gray-500 uppercase tracking-wide">Empaques Flexibles</div>
                    </>
                  )
                }
              </td>
              <td rowSpan={2} className="text-center">
                <span className="font-extrabold text-[17px] tracking-widest uppercase text-gray-900">Orden de Grabado</span>
              </td>
              <td style={{ width: 80 }}>
                <div className={LABEL}>Código</div>
                <div className="text-[10px] font-bold text-gray-900 whitespace-nowrap">4-PRO-026</div>
              </td>
              <td style={{ width: 80 }}>
                <div className={LABEL}>Edición</div>
                <div className="text-[10px] font-bold text-gray-900 whitespace-nowrap">0-280525</div>
              </td>
            </tr>
            <tr>
              <td colSpan={2} className="text-center">
                <div className={LABEL}>No. Orden de Grabado</div>
                <div className="font-black text-[15px] text-gray-900">{po?.orderNumber || '—'}</div>
              </td>
            </tr>
          </tbody>
        </table>

        {/* CLIENTE / FECHA */}
        <table style={{ marginTop: 0 }}>
          <tbody>
            <tr>
              <td colSpan={3}>
                <span className={LABEL}>Cliente: </span>
                <span className={VALUE}>{po?.customerName || '—'}</span>
              </td>
              <td>
                <div className={LABEL}>Fecha</div>
                <div className={VALUE}>{todayStr}</div>
              </td>
              <td>
                <div className={LABEL}>Fecha para entregar</div>
                <div className={VALUE}>{deliveryStr || '—'}</div>
              </td>
            </tr>
            <tr>
              <td colSpan={4}>
                <span className={LABEL}>Nombre Grabado: </span>
                <span className={VALUE}>{val(po?.productCode)} {val(po?.productCode) && val(po?.productName) ? '—' : ''} {val(po?.productName)}</span>
              </td>
              <td>
                {eg.revisionNumero && <span className="text-[12px] font-bold text-gray-700">{eg.revisionNumero}</span>}
              </td>
            </tr>
          </tbody>
        </table>

        {/* ── ESPECIFICACIÓN DE FORMATO ── */}
        <table className="mt-1">
          <tbody>
            <tr>
              <td colSpan={8} className="text-center bg-gray-100 py-0.5">
                <span className="font-extrabold text-[11px] uppercase tracking-widest text-gray-800">Especificación de Formato</span>
              </td>
            </tr>

            {/* Rodillo Final / Flexo */}
            <tr>
              <td style={{ width: '14%' }}><div className={LABEL}>Rodillo Final</div></td>
              <td style={{ width: '18%' }}>
                <span className={VALUE}>{val(egRodillo) || '—'}</span>
              </td>
              <td style={{ width: '8%' }}><div className={LABEL}>Flexo</div></td>
              <td colSpan={5}>
                <span className={VALUE}>{val(egFlexo) || '—'}</span>
              </td>
            </tr>

            {/* Ancho individual + Bobinas al eje + cálculo */}
            <tr>
              <td><div className={LABEL}>Ancho Individual</div></td>
              <td>
                <span className={VALUE}>{val(egAnchoInd) || '—'}</span>
                {egAnchoInd && <span className="text-[9px] text-gray-500 ml-1">mm</span>}
              </td>
              <td colSpan={2}><div className={LABEL}>Bobinas al Eje</div></td>
              <td style={{ width: '10%' }}>
                <span className={VALUE}>{val(egBobEje) || '—'}</span>
              </td>
              <td colSpan={2} className="text-right bg-gray-50">
                <span className={VALUE}>{anchoTotal || '—'}</span>
              </td>
              <td style={{ width: '5%' }}><span className="text-[9px] text-gray-500">mm</span></td>
            </tr>

            {/* Repetición individual + Bobinas al desarrollo + cálculo */}
            <tr>
              <td><div className={LABEL}>Repetición Individual</div></td>
              <td>
                <span className={VALUE}>{val(egRepInd) || '—'}</span>
                {egRepInd && <span className="text-[9px] text-gray-500 ml-1">mm</span>}
              </td>
              <td colSpan={2}><div className={LABEL}>Bobinas al Desarrollo</div></td>
              <td>
                <span className={VALUE}>{val(egBobDes) || '—'}</span>
              </td>
              <td colSpan={2} className="text-right bg-gray-50">
                <span className={VALUE}>{repTotal || '—'}</span>
              </td>
              <td><span className="text-[9px] text-gray-500">mm</span></td>
            </tr>

            {/* Lineaje + Tipo de impresión */}
            <tr>
              <td><div className={LABEL}>Lineaje</div></td>
              <td colSpan={3}>
                <span className={VALUE}>{eg.lineaje ? `${eg.lineaje} LÍNEAS` : '—'}</span>
              </td>
              <td><div className={LABEL}>Tipo Impresión</div></td>
              <td colSpan={3}>
                <span className={VALUE}>{val(eg.tipoPrint) || '—'}</span>
              </td>
            </tr>

            {/* Espesor */}
            <tr>
              <td><div className={LABEL}>Espesor Material</div></td>
              <td colSpan={7}>
                <span className={VALUE}>{eg.espesorMaterial ? `${eg.espesorMaterial} μm` : '—'}</span>
              </td>
            </tr>

            {/* Fotocelda */}
            <tr>
              <td><div className={LABEL}>Fotocelda</div></td>
              <td style={{ width: '6%' }}><div className={LABEL}>Medida</div></td>
              <td colSpan={3}>
                <span className={VALUE}>
                  {hasPhotocell
                    ? `Ancho ${ps.photocell?.width}mm × Alto ${ps.photocell?.height}mm`
                    : (val(eg.especFotocelda) || '—')}
                </span>
              </td>
              <td colSpan={3} className="text-center">
                <span className={VALUE}>
                  {eg.fotoceldaColor === 'COLOR' && eg.fotoceldaColorNombre
                    ? `COLOR (${eg.fotoceldaColorNombre})`
                    : (val(eg.fotoceldaColor) || '—')}
                </span>
              </td>
            </tr>

            {/* Código de barras — puede haber varios (un rollo con varios
                diseños/sabores); se listan uno por línea, tal cual se
                capturaron, en vez de correr juntos separados por comas. */}
            <tr>
              <td colSpan={2} className="text-center"><div className={LABEL}>Código de Barras</div></td>
              <td colSpan={6}>
                {egBarCode
                  ? egBarCode.split(',').map((c: string) => c.trim()).filter(Boolean).map((code: string, i: number) => (
                      <div key={i} className="font-black text-[14px] text-gray-900 tracking-widest">
                        {code}
                      </div>
                    ))
                  : <span className="font-black text-[14px] text-gray-900 tracking-widest">—</span>}
              </td>
            </tr>

          </tbody>
        </table>

        {/* ── COLORES ── (3 rows × 3 cols matching physical document) */}
        <table className="mt-1">
          <tbody>
            <tr>
              <td colSpan={3} className="text-center bg-gray-100 py-0.5">
                <span className="font-extrabold text-[11px] uppercase tracking-widest text-gray-800">Colores</span>
              </td>
            </tr>
            {/* Row 1: positions 1, 4, 7 → indices 0, 3, 6 */}
            <tr>
              <td style={{ width: '33.3%' }}>
                <span className="font-extrabold text-[12px] text-gray-500 mr-2">1</span>
                <span className={VALUE}>{val(colores[0]) || <span className="text-gray-400 italic">N/A</span>}</span>
              </td>
              <td style={{ width: '33.3%' }}>
                <span className="font-extrabold text-[12px] text-gray-500 mr-2">4</span>
                <span className={VALUE}>{val(colores[3]) || <span className="text-gray-400 italic">N/A</span>}</span>
              </td>
              <td style={{ width: '33.3%' }}>
                <span className="font-extrabold text-[12px] text-gray-500 mr-2">7</span>
                <span className={VALUE}>{val(colores[6]) || <span className="text-gray-400 italic">N/A</span>}</span>
              </td>
            </tr>
            {/* Row 2: positions 2, 5, 8 → indices 1, 4, 7 */}
            <tr>
              <td>
                <span className="font-extrabold text-[12px] text-gray-500 mr-2">2</span>
                <span className={VALUE}>{val(colores[1]) || <span className="text-gray-400 italic">N/A</span>}</span>
              </td>
              <td>
                <span className="font-extrabold text-[12px] text-gray-500 mr-2">5</span>
                <span className={VALUE}>{val(colores[4]) || <span className="text-gray-400 italic">N/A</span>}</span>
              </td>
              <td>
                <span className="font-extrabold text-[12px] text-gray-500 mr-2">8</span>
                <span className={VALUE}>{val(colores[7]) || <span className="text-gray-400 italic">N/A</span>}</span>
              </td>
            </tr>
            {/* Row 3: positions 3, 6 → indices 2, 5 */}
            <tr>
              <td>
                <span className="font-extrabold text-[12px] text-gray-500 mr-2">3</span>
                <span className={VALUE}>{val(colores[2]) || <span className="text-gray-400 italic">N/A</span>}</span>
              </td>
              <td>
                <span className="font-extrabold text-[12px] text-gray-500 mr-2">6</span>
                <span className={VALUE}>{val(colores[5]) || <span className="text-gray-400 italic">N/A</span>}</span>
              </td>
              <td></td>
            </tr>
          </tbody>
        </table>

        {/* ── OBSERVACIONES ── */}
        <table className="mt-1">
          <tbody>
            <tr>
              <td style={{ width: 90, verticalAlign: 'top' }}><div className={LABEL}>Observaciones:</div></td>
              <td style={{ minHeight: 50, verticalAlign: 'top', paddingBottom: 16 }}>
                <span className="text-[13px] text-gray-800">{val(eg.observaciones)}</span>
              </td>
              <td style={{ width: 140, textAlign: 'center', verticalAlign: 'middle' }}>
                {eg.cobrarGrabados && (
                  <span className="font-extrabold text-[19px] uppercase text-black tracking-wide border-2 border-black px-2 py-0.5 rounded">
                    ✓ Cobrar Grabados
                  </span>
                )}
              </td>
            </tr>
          </tbody>
        </table>

        {/* ── FIRMAS ── */}
        <table className="mt-3">
          <tbody>
            <tr>
              <td style={{ width: '33%', paddingTop: 36, textAlign: 'center', verticalAlign: 'bottom' }}>
                <div style={{ borderTop: '1px solid #555', paddingTop: 3 }}>
                  <div className="text-[11px] font-bold text-gray-700 uppercase tracking-wide">Solicita</div>
                  <div className="text-[10px] text-gray-500">Gerente General</div>
                </div>
              </td>
              <td style={{ width: '33%', paddingTop: 36, textAlign: 'center', verticalAlign: 'bottom' }}>
                <div style={{ borderTop: '1px solid #555', paddingTop: 3 }}>
                  <div className="text-[11px] font-bold text-gray-700 uppercase tracking-wide">Revisa Datos</div>
                  <div className="text-[10px] text-gray-500">Gerente de Operación</div>
                </div>
              </td>
              <td style={{ width: '33%', paddingTop: 36, textAlign: 'center', verticalAlign: 'bottom' }}>
                <div style={{ borderTop: '1px solid #555', paddingTop: 3 }}>
                  <div className="text-[11px] font-bold text-gray-700 uppercase tracking-wide">Recibe Orden</div>
                  <div className="text-[10px] text-gray-500">Diseñador</div>
                  <div className="text-[10px] text-gray-500 mt-1">Fecha: ___________</div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <div className="mt-2 text-[9px] text-gray-400 text-right no-print">
          Generado: {new Date().toLocaleString('es-MX')}
        </div>
      </div>
    </div>
  );
}
